import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";

// 上部ナビ (PC 主軸) と 下部ナビ (SP 主軸) で使う。
// SP 下部ナビは「アプリっぽい」体験のため、アイコン + 短いラベルで
// 最大 5 項目。D-1 絵文字暫定、将来 heroicons 等に差し替え可能。
interface NavItem {
  to: string;
  label: string;
  icon: string; // 絵文字暫定
  end?: boolean;
}
const navItems: NavItem[] = [
  { to: "/", label: "トップ", icon: "🏠", end: true },
  { to: "/events", label: "イベント", icon: "🗓" },
  { to: "/contents", label: "コンテンツ", icon: "🎥" },
  { to: "/notices", label: "お知らせ", icon: "📢" },
  { to: "/perks", label: "会員特典", icon: "🎁" },
  { to: "/mypage", label: "マイページ", icon: "👤" },
];

export function MemberLayout() {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  const isSuperAdmin = me?.user.roles.some((r) =>
    r === "super_admin",
  );
  const isAdmin = me?.user.roles.some((r) =>
    ["admin", "super_admin", "staff"].includes(r),
  );

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className="bg-white border-b-2 border-tora"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mx-auto max-w-7xl px-4 py-2 flex items-center gap-4">
          <Link to="/" className="shrink-0">
            <img
              src="/logo-header.jpg"
              alt="令和の虎 経営塾"
              className="h-10 md:h-12 w-auto"
            />
          </Link>
          <nav
            className="hidden md:flex items-center gap-4 ml-4"
            aria-label="主要ナビ"
          >
            {/* PC top nav では /mypage を除外 (右上の名前クリックで /mypage に行けるため) */}
            {navItems
              .filter((n) => n.to !== "/mypage")
              .map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    `relative text-sm pb-1 transition-colors ${
                      isActive
                        ? "text-tora font-semibold after:absolute after:bottom-[-14px] after:left-0 after:right-0 after:h-[3px] after:bg-tora after:rounded-full"
                        : "text-tora-ink/70 hover:text-tora"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm">
            {isSuperAdmin && (
              <Link
                to="/admin"
                className="text-tora-ink/70 hover:text-tora"
              >
                管理
              </Link>
            )}
            {isAdmin && !isSuperAdmin && (
              <Link
                to="/admin"
                className="text-tora-ink/70 hover:text-tora"
              >
                管理者
              </Link>
            )}
            <Link
              to="/mypage"
              className="hidden sm:inline text-tora-ink/70 hover:text-tora transition-colors"
              title="マイページを開く"
            >
              マイページ
            </Link>
            <button
              className="text-tora-ink/70 hover:text-tora"
              onClick={async () => {
                await logout();
                nav("/login", { replace: true });
              }}
            >
              ログアウト
            </button>
          </div>
        </div>
      </header>

      <main className="relative flex-1 mx-auto w-full max-w-7xl px-4 py-8 md:py-12 pb-24 md:pb-12 overflow-x-clip">
        {/* 虎イラスト透かし (全ページ共通) */}
        <div
          className="pointer-events-none absolute top-20 right-0 w-[600px] h-[700px] md:w-[700px] md:h-[850px] opacity-[0.12] bg-no-repeat bg-contain bg-right-top invert"
          style={{ backgroundImage: "url('/tiger-bg.png')" }}
          aria-hidden="true"
        />
        {/* 本文は透かしより前面 (z-10)。透かしは背景としてのみ薄く見える。 */}
        <div className="relative z-10">
          {me?.member?.status === "payment_failed" && (
            <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              決済に失敗しています。マイページから決済を更新してください。
            </div>
          )}
          <Outlet />
        </div>
      </main>

      <footer className="border-t border-black/10 py-8 text-center text-sm text-tora-ink/50 hidden md:block">
        <div className="flex items-center justify-center gap-4">
          <span>© 令和の虎 経営塾</span>
          <Link to="/terms" className="hover:text-tora hover:underline">利用規約</Link>
          <a href="mailto:info@reitorakeiei.com" className="hover:text-tora hover:underline">お問い合わせ</a>
        </div>
      </footer>

      {/* SP 下部固定ナビ (md 以上では非表示)。iOS safe-area を考慮。
          F-6: アプリ感を上げるため active indicator / 大タップ領域 / press 感 を追加。 */}
      <nav
        className="fixed bottom-0 inset-x-0 md:hidden bg-tora text-white shadow-[0_-4px_12px_rgba(0,0,0,0.15)] z-40"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        aria-label="モバイル主要ナビ"
      >
        <ul
          className="mx-auto max-w-7xl grid"
          style={{
            gridTemplateColumns: `repeat(${navItems.length}, minmax(0, 1fr))`,
          }}
        >
          {navItems.map((n) => (
            <li key={n.to}>
              <NavLink
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center gap-1 min-h-[64px] px-1 text-[10px] leading-tight transition-all duration-150 active:scale-95 ${
                    isActive
                      ? "text-white font-semibold bg-white/10"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {/* active indicator: 上辺の白い細線 */}
                    {isActive && (
                      <span
                        aria-hidden="true"
                        className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-white rounded-b-full"
                      />
                    )}
                    <span
                      className="text-xl leading-none"
                      aria-hidden="true"
                    >
                      {n.icon}
                    </span>
                    <span>{n.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
